import { IExtendedGrid, IExtendedGridItem } from '@/interfaces/grid.interfaces';

import deepCopy from './deepCopy';
import instanceOfGrid from './instanceOfGrid';

type TVars = {
  // Флаг, который отвечает за нахождение элемента
  // с указанным идентификатором
  isExist: boolean;
};

const mergeGridItems = <Item extends IExtendedGridItem>(
  gridItems: (Item | IExtendedGrid<Item>)[],
  item: Item | IExtendedGrid<Item>,
): (Item | IExtendedGrid<Item>)[] => {
  if (!item.id) {
    return gridItems;
  }

  const mergedGridItems = gridItems.reduce(
    (accumulatedGridItems, gridItem) => {
      if (gridItem.id === item.id) {
        mergeGridItems.vars.isExist = true;

        if (item.rejected) {
          return accumulatedGridItems;
        }

        if (instanceOfGrid(gridItem) && instanceOfGrid(item)) {
          const { items } = item;

          const overrideItems = items.reduce(mergeGridItems.withInsert, [
            ...gridItem.items,
          ]);

          const resolvedGrid: IExtendedGrid<Item> = {
            ...gridItem,
            ...item,
            items: overrideItems,
          };

          mergeGridItems.vars.isExist = true;

          return [...accumulatedGridItems, resolvedGrid];
        }

        // Слияние элемента из конфигурации по умолчанию
        // и элемента из конфигурации при инициализации
        const resolvedItem = {
          ...gridItem,
          ...item,
        } as Item | IExtendedGrid<Item>;

        // Если элемент изначально являвшийся IGrid необходимо переделать
        // на IExtendedGridItem, то необходимо удалить поле с вложенными элементами
        const isConversionFromGridToItem =
          instanceOfGrid(resolvedItem) && typeof resolvedItem.items === 'undefined';

        if (isConversionFromGridToItem) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { items, ...resolvedItemWithoutItems } = resolvedItem;
          return [...accumulatedGridItems, resolvedItemWithoutItems as Item];
        }

        return [...accumulatedGridItems, resolvedItem];
      }
      const isGrid = instanceOfGrid(gridItem);
      if (isGrid) {
        return [
          ...accumulatedGridItems,
          {
            ...gridItem,
            items: mergeGridItems(gridItem.items, item),
          } as IExtendedGrid<Item>,
        ];
      }
      return [...accumulatedGridItems, gridItem];
    },
    [] as (Item | IExtendedGrid<Item>)[],
  );

  return mergedGridItems;
};

const defaultVars: TVars = {
  isExist: false,
};

mergeGridItems.vars = defaultVars;

mergeGridItems.withInsert = <Item extends IExtendedGridItem>(
  gridItems: (Item | IExtendedGrid<Item>)[],
  item: Item | IExtendedGrid<Item>,
): (Item | IExtendedGrid<Item>)[] => {
  mergeGridItems.vars = deepCopy(defaultVars);

  const result = mergeGridItems(gridItems, item);

  if (!mergeGridItems.vars.isExist) {
    result.push(item);
  }

  mergeGridItems.vars = deepCopy(defaultVars);

  return result;
};

function mergeGrid<Item extends IExtendedGridItem>(
  defaultGrid: IExtendedGrid<Item>,
  grid?: IExtendedGrid<Item>,
) {
  if (!grid) {
    return defaultGrid;
  }

  const { id, items } = grid;

  if (id) {
    return grid;
  }

  const overrideItems = items.reduce(mergeGridItems.withInsert, [...defaultGrid.items]);

  const resolvedGrid: IExtendedGrid<Item> = {
    ...defaultGrid,
    ...grid,
    items: overrideItems,
  };

  return resolvedGrid;
}

export default mergeGrid;
